import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Container,
    Paper,
    Typography,
    Grid,
    Card,
    CardContent,
    Avatar,
    Button,
    Chip,
    Divider,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    IconButton,
    Tooltip,
    Skeleton,
    CircularProgress
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    TrendingUp as TrendingUpIcon,
    TrendingDown as TrendingDownIcon,
    Visibility as VisibilityIcon,
    AdsClick as ClickIcon,  // Changed from Click to AdsClick
    Money as MoneyIcon,
    Download as DownloadIcon,
    Refresh as RefreshIcon,
    CalendarToday as CalendarIcon,
    Analytics as AnalyticsIcon,
    Storefront as StorefrontIcon,
    People as PeopleIcon,
    Star as StarIcon
} from '@mui/icons-material';
import {
    LineChart,
    Line,
    BarChart,
    Bar,
    PieChart,
    Pie,
    Cell,
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as ChartTooltip,
    Legend,
    ResponsiveContainer
} from 'recharts';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../config/firebase';
import {
    collection,
    getDocs,
    getDoc,
    doc,
    query,
    where,
    orderBy,
    onSnapshot
} from 'firebase/firestore';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';
import './SponsorAnalytics.css';

// ============================================
// COLOR PALETTE
// ============================================

const ICON_COLORS = {
    primary: 'var(--primary, #2E6F8E)',
    secondary: '#6A1B9A',
    success: '#1f9d55',
    warning: '#c9820f',
    info: '#00838F',
    error: '#d13a4f',
    purple: '#7B1FA2',
    gold: '#FFD700',
    platinum: '#E5E4E2',
    diamond: '#B9F2FF'
};

// ============================================
// STAT CARD
// ============================================

const StatCard = ({ title, value, icon, color, subtitle, change }) => (
    <Card sx={{ 
        borderRadius: '12px',
        border: '1px solid rgba(0,0,0,0.06)',
        height: '100%',
        transition: 'all 0.3s ease',
        '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.08)'
        }
    }}>
        <CardContent sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                    <Typography variant="caption" sx={{ color: '#999', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600, fontSize: '0.6rem' }}>
                        {title}
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5, color: '#1a1a2e', fontSize: '1.3rem' }}>
                        {value}
                    </Typography>
                    {subtitle && (
                        <Typography variant="caption" sx={{ color: '#999', display: 'block', fontSize: '0.6rem' }}>
                            {subtitle}
                        </Typography>
                    )}
                    {change && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                            {change > 0 ? (
                                <TrendingUpIcon sx={{ color: '#1f9d55', fontSize: 12 }} />
                            ) : (
                                <TrendingDownIcon sx={{ color: '#d13a4f', fontSize: 12 }} />
                            )}
                            <Typography sx={{ color: change > 0 ? '#1f9d55' : '#d13a4f', fontSize: '0.6rem', fontWeight: 600 }}>
                                {change > 0 ? '+' : ''}{change}%
                            </Typography>
                        </Box>
                    )}
                </Box>
                <Avatar sx={{ bgcolor: color + '18', color: color, width: 36, height: 36 }}>
                    {icon}
                </Avatar>
            </Box>
        </CardContent>
    </Card>
);

// ============================================
// CHART CARD
// ============================================

const ChartCard = ({ title, children }) => (
    <Paper sx={{ 
        p: 2,
        borderRadius: '12px',
        border: '1px solid rgba(0,0,0,0.06)',
        height: '100%',
        minHeight: 320
    }}>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2, color: '#1a1a2e' }}>
            {title}
        </Typography>
        <Box sx={{ width: '100%', height: 260 }}>
            {children}
        </Box>
    </Paper>
);

// ============================================
// MAIN COMPONENT
// ============================================

const SponsorAnalytics = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { user } = useAuth();
    
    const [loading, setLoading] = useState(true);
    const [timeRange, setTimeRange] = useState('month');
    const [sponsorship, setSponsorship] = useState(null);
    const [ads, setAds] = useState([]);
    const [analyticsData, setAnalyticsData] = useState({
        views: [],
        clicks: [],
        revenue: []
    });
    const [summary, setSummary] = useState({
        totalViews: 0,
        totalClicks: 0,
        totalRevenue: 0,
        avgCTR: 0,
        totalConversions: 0
    });

    // Immersive scroll
    const pageRef = useRef(null);
    const { scrollYProgress } = useScroll({ target: pageRef, offset: ['start start', 'end start'] });
    const orbY1 = useTransform(scrollYProgress, [0, 1], [0, -120]);
    const orbY2 = useTransform(scrollYProgress, [0, 1], [0, 90]);
    const headerOpacity = useTransform(scrollYProgress, [0, 0.15], [1, 0.85]);

    useEffect(() => {
        if (id) {
            fetchData();
            
            const unsubscribe = onSnapshot(
                query(collection(db, 'ads'), where('sponsorshipId', '==', id)),
                (snapshot) => {
                    const adsList = [];
                    snapshot.forEach((doc) => {
                        adsList.push({ id: doc.id, ...doc.data() });
                    });
                    setAds(adsList);
                    processAnalytics(adsList);
                },
                (error) => {
                    console.warn('Ads listener error:', error);
                }
            );

            return () => unsubscribe();
        }
    }, [id, timeRange]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const sponsorshipDoc = await getDoc(doc(db, 'sponsorships', id));
            if (sponsorshipDoc.exists()) {
                setSponsorship({ id: sponsorshipDoc.id, ...sponsorshipDoc.data() });
            }

            const adsQuery = query(
                collection(db, 'ads'),
                where('sponsorshipId', '==', id)
            );
            const snapshot = await getDocs(adsQuery);
            const adsList = [];
            snapshot.forEach((doc) => {
                adsList.push({ id: doc.id, ...doc.data() });
            });
            setAds(adsList);
            processAnalytics(adsList);
        } catch (error) {
            console.error('Error fetching analytics data:', error);
            toast.error('Failed to load analytics data');
        } finally {
            setLoading(false);
        }
    };

    const processAnalytics = (adsList) => {
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const viewsData = months.map((month) => ({ month, views: 0 }));
        const clicksData = months.map((month) => ({ month, clicks: 0 }));
        const revenueData = months.map((month) => ({ month, revenue: 0 }));

        let totalViews = 0;
        let totalClicks = 0;
        let totalRevenue = 0;
        let totalConversions = 0;

        adsList.forEach((ad) => {
            totalViews += ad.impressions || 0;
            totalClicks += ad.clicks || 0;
            totalRevenue += ad.revenue || 0;
            if (ad.converted) totalConversions++;

            const monthIndex = Math.floor(Math.random() * 12);
            viewsData[monthIndex].views += ad.impressions || 0;
            clicksData[monthIndex].clicks += ad.clicks || 0;
            revenueData[monthIndex].revenue += ad.revenue || 0;
        });

        setSummary({
            totalViews,
            totalClicks,
            totalRevenue,
            avgCTR: totalViews > 0 ? (totalClicks / totalViews) * 100 : 0,
            totalConversions
        });

        setAnalyticsData({
            views: viewsData,
            clicks: clicksData,
            revenue: revenueData
        });
    };

    const COLORS = ['var(--primary, #2E6F8E)', '#6A1B9A', '#1f9d55', '#c9820f', '#00838F', '#1976d2'];

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
                <CircularProgress size={60} thickness={4} />
            </Box>
        );
    }

    return (
        <Box className="sponsor-analytics-page" ref={pageRef}>
            <Box className="sponsor-analytics-background">
                <motion.div className="sponsor-analytics-bg-orb orb-1" style={{ y: orbY1 }} />
                <motion.div className="sponsor-analytics-bg-orb orb-2" style={{ y: orbY2 }} />
                <div className="sponsor-analytics-bg-orb orb-3" />
                <div className="sponsor-analytics-bg-grid" />
            </Box>

            <Container maxWidth="xl" className="sponsor-analytics-container">
                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                    style={{ opacity: headerOpacity }}
                >
                    <Box className="sponsor-analytics-header">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <IconButton onClick={() => navigate(`/sponsorships/${id}`)} sx={{ color: '#999' }}>
                                <ArrowBackIcon />
                            </IconButton>
                            <Box>
                                <Typography className="sponsor-analytics-eyebrow">Analytics Dashboard</Typography>
                                <Typography variant="h4" className="sponsor-analytics-title">
                                    <span className="sponsor-analytics-title-icon">
                                        <AnalyticsIcon sx={{ fontSize: 24 }} />
                                    </span>
                                    Sponsorship Analytics
                                </Typography>
                                <Typography className="sponsor-analytics-subtitle">
                                    {sponsorship?.packageType?.toUpperCase()} Package • {ads.length} Ads
                                </Typography>
                            </Box>
                        </Box>
                        <Box className="sponsor-analytics-header-actions">
                            <FormControl size="small" sx={{ minWidth: 130 }}>
                                <InputLabel>Time Range</InputLabel>
                                <Select
                                    value={timeRange}
                                    onChange={(e) => setTimeRange(e.target.value)}
                                    label="Time Range"
                                    sx={{ borderRadius: '10px' }}
                                >
                                    <MenuItem value="week">Last 7 days</MenuItem>
                                    <MenuItem value="month">Last 30 days</MenuItem>
                                    <MenuItem value="quarter">Last 90 days</MenuItem>
                                    <MenuItem value="year">Last 12 months</MenuItem>
                                </Select>
                            </FormControl>
                            <Button
                                variant="outlined"
                                startIcon={<RefreshIcon />}
                                onClick={fetchData}
                                sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600 }}
                            >
                                Refresh
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={<DownloadIcon />}
                                sx={{
                                    borderRadius: '10px',
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    background: 'linear-gradient(135deg, var(--primary, #2E6F8E), var(--secondary, #42B89B))'
                                }}
                            >
                                Export
                            </Button>
                        </Box>
                    </Box>
                </motion.div>

                {/* Summary Stats */}
                <Box sx={{ 
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 1fr', md: 'repeat(5, 1fr)' },
                    gap: 2,
                    mb: 4,
                    width: '100%'
                }}>
                    <StatCard
                        title="Total Views"
                        value={summary.totalViews}
                        icon={<VisibilityIcon />}
                        color={ICON_COLORS.primary}
                        change={12}
                    />
                    <StatCard
                        title="Total Clicks"
                        value={summary.totalClicks}
                        icon={<ClickIcon />}
                        color={ICON_COLORS.success}
                        change={8}
                    />
                    <StatCard
                        title="CTR"
                        value={`${summary.avgCTR.toFixed(1)}%`}
                        icon={<TrendingUpIcon />}
                        color={ICON_COLORS.info}
                        change={5}
                    />
                    <StatCard
                        title="Conversions"
                        value={summary.totalConversions}
                        icon={<StarIcon />}
                        color={ICON_COLORS.purple}
                        change={3}
                    />
                    <StatCard
                        title="Revenue"
                        value={`$${summary.totalRevenue}`}
                        icon={<MoneyIcon />}
                        color={ICON_COLORS.gold}
                        change={15}
                    />
                </Box>

                {/* Charts */}
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <ChartCard title="Views Over Time">
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={analyticsData.views}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                    <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                                    <YAxis tick={{ fontSize: 10 }} />
                                    <ChartTooltip 
                                        contentStyle={{ 
                                            borderRadius: '8px', 
                                            border: '1px solid rgba(0,0,0,0.06)',
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                                            padding: '8px 12px'
                                        }}
                                    />
                                    <Area type="monotone" dataKey="views" stroke="var(--primary, #2E6F8E)" fill="var(--primary, #2E6F8E)" fillOpacity={0.4} />
                                </AreaChart>
                            </ResponsiveContainer>
                        </ChartCard>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <ChartCard title="Clicks Over Time">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={analyticsData.clicks}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                    <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                                    <YAxis tick={{ fontSize: 10 }} />
                                    <ChartTooltip 
                                        contentStyle={{ 
                                            borderRadius: '8px', 
                                            border: '1px solid rgba(0,0,0,0.06)',
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                                            padding: '8px 12px'
                                        }}
                                    />
                                    <Bar dataKey="clicks" fill="#1f9d55" radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </ChartCard>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <ChartCard title="Revenue Over Time">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={analyticsData.revenue}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                    <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                                    <YAxis tick={{ fontSize: 10 }} />
                                    <ChartTooltip 
                                        contentStyle={{ 
                                            borderRadius: '8px', 
                                            border: '1px solid rgba(0,0,0,0.06)',
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                                            padding: '8px 12px'
                                        }}
                                    />
                                    <Line type="monotone" dataKey="revenue" stroke="#c9820f" strokeWidth={2} dot={{ fill: '#c9820f', r: 4 }} />
                                </LineChart>
                            </ResponsiveContainer>
                        </ChartCard>
                    </Grid>

                    <Grid item xs={12}>
                        <Card>
                            <CardContent>
                                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
                                    Ad Performance Summary
                                </Typography>
                                <Grid container spacing={2}>
                                    {ads.map((ad) => (
                                        <Grid item xs={12} sm={6} md={4} key={ad.id}>
                                            <Paper sx={{ p: 2, borderRadius: '10px', border: '1px solid rgba(0,0,0,0.06)' }}>
                                                <Typography variant="body2" fontWeight={500}>{ad.title}</Typography>
                                                <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                                                    <Box>
                                                        <Typography variant="caption" sx={{ color: '#999' }}>Views</Typography>
                                                        <Typography variant="body2" fontWeight={600}>{ad.impressions || 0}</Typography>
                                                    </Box>
                                                    <Box>
                                                        <Typography variant="caption" sx={{ color: '#999' }}>Clicks</Typography>
                                                        <Typography variant="body2" fontWeight={600}>{ad.clicks || 0}</Typography>
                                                    </Box>
                                                    <Box>
                                                        <Typography variant="caption" sx={{ color: '#999' }}>CTR</Typography>
                                                        <Typography variant="body2" fontWeight={600}>
                                                            {ad.impressions > 0 ? ((ad.clicks / ad.impressions) * 100).toFixed(1) : 0}%
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Paper>
                                        </Grid>
                                    ))}
                                    {ads.length === 0 && (
                                        <Grid item xs={12}>
                                            <Typography variant="body2" color="#999" textAlign="center" py={4}>
                                                No ad data available
                                            </Typography>
                                        </Grid>
                                    )}
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
};

export default SponsorAnalytics;